import React, { useEffect, useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { useAuth } from '../../context/AuthContext';
import { getEvents, getEventEmployees, getPaginatedEvents } from '../../services/supabase';
import EventCard from '../../components/Events/EventCard';
import { CalendarDays, List, LandPlot, Triangle, Info } from 'lucide-react';
import EventStatusFilters from '../../components/Events/EventStatusFilters';
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import { PrimaryButton, CloseButton } from '../../components/Common/BaseButton';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import startOfWeek from 'date-fns/startOfWeek';
import getDay from 'date-fns/getDay';
import enUS from 'date-fns/locale/en-US';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { getEventStatusColors } from '../../utils/utils';
import { useLocation } from 'react-router-dom';
import AddNewEventButton from '../../components/ManagerPermission/AddNewEventButton';
import { useRoleContext } from '../../context/RoleContext';
import { DateTime } from 'luxon';
import Pagination from '../../components/Common/Pagination';
import EmployeeFilterChip from '../../components/Events/EmployeeFilterChip';

const PageContainer = styled.div`
  padding: 1rem;
  // max-width: 1200px;
  margin: 0 5rem;
  
  @media (max-width: 768px) {
    padding: 0.5rem;
    margin: 0 auto;
  }
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    align-items: flex-start;
    flex-wrap: wrap;
  }
`;

const PageTitle = styled.h2`
  font-size: 1.8rem;
  color: #333;
  margin: 0;
`;

const SearchRow = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 0.7rem 1rem;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 1rem;
`;

const EventsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
`;

const FilterChipsRow = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: .5rem;
  flex-wrap: wrap;
  align-items: center;
  position: relative; /* Add this to establish stacking context */

  @media (max-width: 600px) {
    gap: 0.4rem;
    padding-bottom: 0.5rem;
    // margin-bottom: 1rem;
    // overflow-x: auto;
    overflow-y: visible; /* Add this to allow dropdown to show */
  }
`;

const FilterChip = styled.button`
  background: ${({ $active }) => ($active ? 'var(--primary-green-light, #e6f4ea)' : 'white')};
  color: ${({ $active }) => ($active ? 'var(--primary-green, #2D882D)' : '#374151')};
  border: 1px solid ${({ $active }) => ($active ? 'var(--primary-green, #2D882D)' : '#e5e7eb')};
  border-radius: 999px;
  padding: 0.4rem 0.8rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  white-space: nowrap;
  box-shadow: ${({ $active }) => ($active ? '0 1px 2px rgba(0,0,0,0.05)' : 'none')};

  &:hover {
    background: ${({ $active }) => ($active ? 'var(--primary-green-light, #e6f4ea)' : '#f9fafb')};
    border-color: ${({ $active }) => ($active ? 'var(--primary-green, #2D882D)' : '#d1d5db')};
  }

  &:active {
    transform: translateY(1px);
  }

  @media (max-width: 600px) {
    padding: 0.3rem 0.6rem;
    font-size: 0.875rem;
  }
`;

const CustomRangeContainer = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  margin-bottom: 1rem;
  flex-wrap: wrap;
  background: #f8fafb;
  border-radius: 16px;
  padding: 1rem 1.2rem;
  box-shadow: 0 1px 4px rgba(0,0,0,0.03);
`;

const DateLabel = styled.label`
  display: flex;
  flex-direction: column;
  font-size: 0.98rem;
  color: #333;
  font-weight: 500;
  gap: 0.3rem;
`;

const DateInput = styled.input`
  border: 1px solid #d1e3dd;
  border-radius: 8px;
  padding: 0.5rem 1rem;
  font-size: 1rem;
  background: #fff;
  color: #222;
  transition: border 0.2s;
  outline: none;
  min-width: 140px;

  &:focus {
    border-color: var(--primary-green, #2D882D);
    box-shadow: 0 0 0 2px #e6f4ea;
  }

  @media (max-width: 600px) {
    min-width: 100px;
    font-size: 0.95rem;
    padding: 0.4rem 0.7rem;
  }
`;

const Switcher = styled.div`
  display: flex;
  background: #f5f5f5;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e0e0e0;
  width: fit-content;
`;

const SwitchLabel = styled.label`
  flex: 1;
  padding: 0.5rem 1.2rem;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 600;
  color: ${({ checked }) => (checked ? '#2D882D' : '#888')};
  background: ${({ checked }) => (checked ? '#fff' : 'transparent')};
  display: flex;
  align-items: center;
  gap: 6px;
  border: none;
  user-select: none;
  &:not(:last-child) {
    border-right: 1px solid #e0e0e0;
  }
`;

const SwitchInput = styled.input`
  display: none;
`;

// Desktop-only and Mobile-only wrappers
const DesktopOnly = styled.div`
  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileOnly = styled.div`
  display: none;
  @media (max-width: 768px) {
    display: block;
  }
`;

// Bottom sheet + overlay (mobile)
const SheetOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.4);
  z-index: 5000;
  opacity: ${({ open }) => (open ? 1 : 0)};
  pointer-events: ${({ open }) => (open ? 'auto' : 'none')};
  transition: opacity 180ms ease;
  will-change: opacity;
`;

const BottomSheet = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 5001;
  background: #fff;
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
  box-shadow: 0 -10px 30px rgba(0,0,0,0.15);
  transform: translateY(${({ open }) => (open ? '0%' : '100%')});
  transition: transform 220ms ease;
  will-change: transform;
  height: 70vh;
  padding-bottom: calc(env(safe-area-inset-bottom) + 16px);
  @media (min-width: 769px) {
    display: none;
  }
`;

const SheetGrabber = styled.div`
  width: 44px;
  height: 5px;
  background: #e5e7eb;
  border-radius: 999px;
  margin: 10px auto 6px;
`;

const SheetHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 1rem 0.75rem;
  border-bottom: 1px solid #f0f0f0;
`;

const SheetTitle = styled.div`
  font-weight: 800;
  font-size: 1.05rem;
  color: #111827;
`;

const SheetContent = styled.div`
  padding: 0.8rem 1rem;
  overflow-y: auto;
  height: calc(70vh - 58px - env(safe-area-inset-bottom));
`;

const Section = styled.div`
  margin-bottom: 1rem;
`;

const SectionTitle = styled.div`
  font-weight: 700;
  font-size: 0.95rem;
  color: #374151;
  margin-bottom: 0.55rem;
`;

// Mobile filters bar under search input
const MobileFiltersBar = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  margin-bottom: 0.75rem;
  flex-wrap: wrap;
  @media (min-width: 769px) {
    display: none;
  }
`;

const ChipClose = styled.span`
  margin-left: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 999px;
  background: rgba(0,0,0,0.06);
  color: #374151;
  font-weight: 700;
  font-size: 12px;
  line-height: 16px;
  cursor: pointer;
  user-select: none;
  will-change: transform;
`;

const NoResults = styled.div`
  font-style: italic;
  font-size: 1rem;
  text-align: center;
  color: #6b7280;
  padding: 1rem 0;
`;

const InfoBox = styled.div`
  background-color: var(--primary-blue-light);
  border-radius: 8px;
  border: 1px solid var(--primary-blue);
  padding: 0.75rem 1rem;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #1e40af;
  font-weight: 500;
`;

const locales = { 'en-US': enUS };
const localizer = dateFnsLocalizer({ format, parse, startOfWeek, getDay, locales });

const EventsPage = () => {
  const { user } = useAuth();
  const { isManager, isAuthenticated } = useRoleContext();
  const [activeTab, setActiveTab] = useState('my');
  const [allEvents, setAllEvents] = useState([]);
  const [myEvents, setMyEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [eventEmployeesMap, setEventEmployeesMap] = useState({});
  const [search, setSearch] = useState('');
  const [events, setEvents] = useState([]);
  const [totalEvents, setTotalEvents] = useState(0);
  const [currentPageAll, setCurrentPageAll] = useState(1);
  const [currentPageMy, setCurrentPageMy] = useState(1);
  const pageSize = 10;
  const [activeFilter, setActiveFilter] = useState('all');
  const [customRange, setCustomRange] = useState({ start: null, end: null });
  const [statusFilter, setStatusFilter] = useState('all');
  const [eventType, setEventType] = useState('all');
  const [eventTypeFilter, setEventTypeFilter] = useState('all');
  const [viewMode, setViewMode] = useState('list');
  const location = useLocation();
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch all events with their employees in a single query
        const { data: eventsData, error: eventsError } = await getEvents();
        if (eventsError) throw eventsError;
        
        // Process the data to create the employees map
        const employeesMap = {};
        eventsData.forEach(event => {
          employeesMap[event.id] = event.event_employees?.map(emp => ({
            ...emp,
            user: emp.user
          })) || [];
        });
        
        setAllEvents(eventsData || []);
        setEventEmployeesMap(employeesMap);
        
        // Filter to get my events from all events if user exists
        if (user) {
          const myFilteredEvents = eventsData.filter(event => {
            return event.event_employees?.some(
              employee => employee.user_id === user.id
            );
          });
          
          setMyEvents(myFilteredEvents || []);
        }
      } catch (err) {
        setError(err.message || 'Failed to load events.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchEvents();
  }, [user]);

  useEffect(() => {
    setLoading(true);
    getPaginatedEvents(currentPageAll, pageSize, search)
      .then(async ({ data, count }) => {
        // Optionally fetch employees for each event if needed
        const employeePromises = data.map(event => getEventEmployees(event.id));
        const employeeResults = await Promise.all(employeePromises);
        const eventsWithEmployees = data.map((event, idx) => ({
          ...event,
          employees: employeeResults[idx].data || []
        }));
        setEvents(eventsWithEmployees);
        setTotalEvents(count);
        setLoading(false);
      })
      .catch(err => {
        setError(err.message || 'Failed to load events.');
        setLoading(false);
      });
  }, [currentPageAll, pageSize, search]);

  useEffect(() => {
    setCurrentPageAll(1);
    setCurrentPageMy(1);
  }, [search]);

  const uniqueEmployees = useMemo(() => {
    const employeeSet = new Set();
    const employees = [];
    
    Object.values(eventEmployeesMap).forEach(eventEmps => {
      eventEmps.forEach(emp => {
        if (emp.user && emp.user.id && !employeeSet.has(emp.user.id)) {
          employeeSet.add(emp.user.id);
          employees.push({
            id: emp.user.id,
            name: emp.user.name || emp.user.email || 'Unknown'
          });
        }
      });
    });
    
    return employees.sort((a, b) => a.name.localeCompare(b.name));
  }, [eventEmployeesMap]);
  
  const employeeIdToName = useMemo(() => {
    const map = {};
    uniqueEmployees.forEach(emp => {
      map[emp.id] = emp.name;
    });
    return map;
  }, [uniqueEmployees]);

  const customRangeLabel = useMemo(() => {
    if (!customRange || !customRange.start || !customRange.end) return 'Custom Dates';
    const startDt = DateTime.fromJSDate(customRange.start);
    const endDt = DateTime.fromJSDate(customRange.end);
    const sameYear = startDt.year === endDt.year;
    const startFmt = sameYear ? 'MMM d' : 'MMM d, yyyy';
    const endFmt = 'MMM d, yyyy';
    return `${startDt.toFormat(startFmt)} – ${endDt.toFormat(endFmt)}`;
  }, [customRange]);

  const formatStatusLabel = (status) => {
    if (!status) return '';
    // convert ENUM_STYLE or mixed to Title Case with spaces
    const spaced = status.replace(/[_-]+/g, ' ');
    return spaced.replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
  };

  const getEventTypeChipColors = (type) => {
    switch (type) {
      case 'WeGo':
        return { bg: '#f0fdf4', color: '#166534', border: '#34d399' };
      case 'TLG':
        return { bg: '#fef3c7', color: '#92400e', border: '#fbbf24' };
      default:
        return { bg: '#f3f4f6', color: '#374151', border: '#e5e7eb' };
    }
  };


  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const view = params.get('view');
    if (view === 'calendar') setViewMode('calendar');
    else if (view === 'list') setViewMode('list');
  }, [location.search]);

  // Lock body scroll when the mobile filter sheet is open
  useEffect(() => {
    if (isFilterOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [isFilterOpen]);

  const totalPages = Math.ceil(totalEvents / pageSize);

  const filteredEvents = allEvents
    .filter(event => {
      const eventDateTime = DateTime.fromISO(event.event_date).setZone(event.timezone); //could be an issue...
      let matchesDateFilter = true;
      let matchesEventType = true;
      let matchesEmployee = true;

      // Employee Filter - check if any selected employees are assigned to this event
      if (selectedEmployees.length > 0) {
        matchesEmployee = eventEmployeesMap[event.id]?.some(emp => 
          selectedEmployees.includes(emp.user_id)
        );
      }

      // Event Type Filter (My Events vs All Events)
      if (eventTypeFilter === 'my') {
        matchesEventType = eventEmployeesMap[event.id]?.some(emp => emp.user_id === user.id);
      }

      if (eventType === 'TLG') {
        matchesEventType = event.event_type === 'TLG';
      }
      if (eventType === 'WeGo') {
        matchesEventType = event.event_type === 'WeGo';
      }

      // Date Filter
      const now = DateTime.now().setZone(event.timezone);
      const eventStart = DateTime.fromISO(event.start_time).setZone(event.timezone);
      const eventEnd = DateTime.fromISO(event.end_time).setZone(event.timezone);
        switch (activeFilter) {
          case 'today':
            const todayStart = now.startOf('day');
            const todayEnd = now.endOf('day');
            // Check if today overlaps with the event's date range
            matchesDateFilter = eventStart <= todayEnd && eventEnd >= todayStart;
            break;
          case 'next7':
            const next7Start = now.startOf('day');
            const next7End = now.plus({ days: 7 }).endOf('day');
            // Check if the next 7 days overlap with the event's date range
            matchesDateFilter = eventStart <= next7End && eventEnd >= next7Start;
            break;
          case 'month':
            const monthStart = now.startOf('month');
            const monthEnd = now.endOf('month');
            // Check if this month overlaps with the event's date range
            matchesDateFilter = eventStart <= monthEnd && eventEnd >= monthStart;
            break;
          case 'custom':
            if (customRange.start && customRange.end) {
              const rangeStart = DateTime.fromJSDate(customRange.start).setZone(event.timezone).startOf('day');
              const rangeEnd = DateTime.fromJSDate(customRange.end).setZone(event.timezone).endOf('day');
              // Check if the custom date range overlaps with the event's date range
              matchesDateFilter = eventStart <= rangeEnd && eventEnd >= rangeStart;
            }
          break;
        default:
          matchesDateFilter = true;
      }

      // Search filter
      const matchesSearch =
        event.title.toLowerCase().includes(search.toLowerCase()) ||
        (event.location && event.location.toLowerCase().includes(search.toLowerCase())) ||
        (event.company_name && event.company_name.toLowerCase().includes(search.toLowerCase()));

      // Status filter
      const matchesStatus = statusFilter === 'all' || event.event_status === statusFilter;

      return matchesEventType && matchesDateFilter && matchesSearch && matchesStatus && matchesEmployee;
    })
    .sort((a, b) => {
      const timeA = DateTime.fromISO(a.start_time).setZone(a.timezone);
      const timeB = DateTime.fromISO(b.start_time).setZone(b.timezone);
      
      // For date filters, use simple chronological order
      if (['today', 'next7', 'month', 'custom'].includes(activeFilter)) {
        return timeA.toMillis() - timeB.toMillis();
      }
      
      // For "all" filter, use end time to determine if event is still active/upcoming
      const endA_UTC = DateTime.fromISO(a.end_time).setZone(a.timezone).toUTC();
      const endB_UTC = DateTime.fromISO(b.end_time).setZone(b.timezone).toUTC();
      const now_UTC = DateTime.now().toUTC();
      
      // Both events are finished - show most recently finished first
      if (endA_UTC < now_UTC && endB_UTC < now_UTC) {
        return endB_UTC.toMillis() - endA_UTC.toMillis();
      }
      
      // Mixed finished/active - put active events first
      if (endA_UTC < now_UTC) return 1;  // A is finished, B is active - B comes first
      if (endB_UTC < now_UTC) return -1; // B is finished, A is active - A comes first
      
      // Both events are active/upcoming - show soonest starting first
      return timeA.toMillis() - timeB.toMillis();
    });

  const totalPagesMy = Math.ceil(filteredEvents.length / pageSize);
  const paginatedMyEvents = filteredEvents.slice(
    (currentPageMy - 1) * pageSize,
    currentPageMy * pageSize
  );
  // Filter events based on status  
  const filteredEventsStatus = filteredEvents.filter(event => 
    statusFilter === 'all' || event.event_status === statusFilter
  );

  const toggleEventTypeFilter = (type) => {
    setEventTypeFilter(current => current === type ? 'all' : type);
  };

  const toggleEventType = (type) => {
    setEventType(current => current === type ? 'all' : type);
  };

  const toggleDateFilter = (filter) => {
    setActiveFilter(current => current === filter ? 'all' : filter);
    // Clear custom range if toggling off custom filter
    if (filter === 'custom' && activeFilter === 'custom') {
      setCustomRange({ start: null, end: null });
    }
  };

  const calendarEvents = allEvents.map(event => {
    const start = DateTime.fromISO(event.start_time || `${event.event_date}T00:00:00Z`)
      .setZone(event.timezone);
    const end = DateTime.fromISO(event.end_time || `${event.event_date}T23:59:59Z`)
      .setZone(event.timezone);

    // Create "fake" Date objects that represent the event's local time
    // as if it were happening in the user's current timezone
    const startLocal = new Date(
      start.year,
      start.month - 1, 
      start.day,
      start.hour,
      start.minute,
      start.second
    );
    
    const endLocal = new Date(
      end.year,
      end.month - 1,
      end.day,
      end.hour,
      end.minute,
      end.second
    );

    return {
      id: event.id,
      title: event.title,
      start: startLocal, // This will show the event's local time regardless of user's timezone
      end: endLocal,     // This will show the event's local time regardless of user's timezone
      allDay: !event.start_time || !event.end_time,
      resource: event
    };
  });

  const filteredCalendarEvents = calendarEvents.filter(event => {
    let matchesEventType = true;
    let matchesEmployee = true;
    
    // Event Type Filter (My Events vs All Events)
    if (eventTypeFilter === 'my') {
      matchesEventType = eventEmployeesMap[event.resource.id]?.some(emp => emp.user_id === user.id);
    }
    
    // WeGo vs TLG Filter
    if (eventType === 'TLG') {
      matchesEventType = matchesEventType && event.resource.event_type === 'TLG';
    }
    if (eventType === 'WeGo') {
      matchesEventType = matchesEventType && event.resource.event_type === 'WeGo';
    }
  
    // Employee Filter - check if any selected employees are assigned to this event
    if (selectedEmployees.length > 0) {
      matchesEmployee = eventEmployeesMap[event.resource.id]?.some(emp => 
        selectedEmployees.includes(emp.user_id)
      );
    }
  
    const matchesStatus = statusFilter === 'all' || event.resource.event_status === statusFilter;
    const matchesSearch = event.title.toLowerCase().includes(search.toLowerCase());
    
    return matchesEventType && matchesEmployee && matchesStatus && matchesSearch;
  });

  if (loading && activeTab === 'my' && allEvents.length === 0) {
    return <PageContainer><LoadingSpinner text="Loading Events..." /></PageContainer>;
  }

  const handleEmployeeToggle = (employeeId) => {
    setSelectedEmployees(prev => 
      prev.includes(employeeId) 
        ? prev.filter(id => id !== employeeId)
        : [...prev, employeeId]
    );
  };

  const handleRemoveEmployee = (employeeId) => {
    setSelectedEmployees(prev => prev.filter(id => id !== employeeId));
  };

  const clearAllEmployees = () => {
    setSelectedEmployees([]);
  };

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>Events</PageTitle>
          <div style={{ display: 'flex', gap: 8 }}>
            <Switcher>
              <SwitchLabel checked={viewMode === 'list'}>
                <SwitchInput
                  type="radio"
                  name="viewMode"
                  value="list"
                  checked={viewMode === 'list'}
                  onChange={() => setViewMode('list')}
                />
                <List size={18} />
                <span>List</span>
              </SwitchLabel>
              <SwitchLabel checked={viewMode === 'calendar'}>
                <SwitchInput
                  type="radio"
                  name="viewMode"
                  value="calendar"
                  checked={viewMode === 'calendar'}
                  onChange={() => setViewMode('calendar')}
                />
                <CalendarDays size={18} />
                <span>Calendar</span>
              </SwitchLabel>
            </Switcher>
          </div>
        <AddNewEventButton onClick={() => window.location.href = '/events/new'} isManager={isManager} isAuthenticated={isAuthenticated} />
      </PageHeader>
      
      <SearchRow>
        <SearchInput
          type="text"
          placeholder="Search events by name, location, or company..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') e.preventDefault(); }}
        />
      </SearchRow>

      {/* Mobile: Filters button under search with active chips */}
      <MobileFiltersBar>
        <PrimaryButton onClick={() => setIsFilterOpen(true)}>Filters</PrimaryButton>
        {/* Active filter chips with clear (x) actions */}
        {eventTypeFilter !== 'all' && (
          <FilterChip $active onClick={() => setIsFilterOpen(true)}>
            {eventTypeFilter === 'my' ? 'My Events' : 'All Events'}
            <ChipClose
              onClick={e => { e.stopPropagation(); setEventTypeFilter('all'); }}
              aria-label="Clear ownership filter"
            >×</ChipClose>
          </FilterChip>
        )}
        {(['today','next7','month','custom'].includes(activeFilter)) && (
          <FilterChip $active onClick={() => setIsFilterOpen(true)}>
            {activeFilter === 'today' && 'Today'}
            {activeFilter === 'next7' && 'Next 7 Days'}
            {activeFilter === 'month' && 'This Month'}
            {activeFilter === 'custom' && customRangeLabel}
            <ChipClose
              onClick={e => { e.stopPropagation(); setActiveFilter('all'); setCustomRange({ start: null, end: null }); }}
              aria-label="Clear date filter"
            >×</ChipClose>
          </FilterChip>
        )}
        {selectedEmployees.length > 0 && selectedEmployees.map(empId => (
          <FilterChip key={empId} $active onClick={() => setIsFilterOpen(true)}>
            {employeeIdToName[empId] || 'Employee'}
            <ChipClose
              onClick={e => { e.stopPropagation(); handleRemoveEmployee(empId); }}
              aria-label={`Remove ${employeeIdToName[empId] || 'employee'}`}
            >×</ChipClose>
          </FilterChip>
        ))}
        {eventType !== 'all' && (() => {
          const { bg, color, border } = getEventTypeChipColors(eventType);
          const Icon = eventType === 'TLG' ? Triangle : LandPlot;
          return (
            <FilterChip $active style={{ background: bg, color, border: `1px solid ${border}` }} onClick={() => setIsFilterOpen(true)}>
              <Icon size={16} /> {eventType}
              <ChipClose
                onClick={e => { e.stopPropagation(); setEventType('all'); }}
                aria-label="Clear Event Type filter"
              >x</ChipClose>
            </FilterChip>
          );
        })()}
        {statusFilter !== 'all' && (() => {
          const { bg, color, border } = getEventStatusColors(statusFilter);
          return (
            <FilterChip $active style={{ background: bg, color, border: `1px solid ${border}` }} onClick={() => setIsFilterOpen(true)}>
              {formatStatusLabel(statusFilter)}
              <ChipClose
                onClick={e => { e.stopPropagation(); setStatusFilter('all'); }}
                aria-label="Clear status filter"
              >×</ChipClose>
            </FilterChip>
          );
        })()}
      </MobileFiltersBar>
      <MobileOnly>
        {createPortal(
          <>
            <SheetOverlay open={isFilterOpen} onClick={() => setIsFilterOpen(false)} />
            <BottomSheet open={isFilterOpen} role="dialog" aria-modal="true">
              <SheetGrabber />
              <SheetHeader>
                <SheetTitle>Filters</SheetTitle>
                <CloseButton onClick={() => setIsFilterOpen(false)}>Done</CloseButton>
              </SheetHeader>
              <SheetContent>
            <Section>
              <SectionTitle>Ownership</SectionTitle>
              <FilterChipsRow>
                <FilterChip $active={eventTypeFilter === 'all'} onClick={() => toggleEventTypeFilter('all')}>
                  All Events
                </FilterChip>
                <FilterChip $active={eventTypeFilter === 'my'} onClick={() => toggleEventTypeFilter('my')}>
                  My Events
                </FilterChip>
              </FilterChipsRow>
            </Section>

            <Section>
              <SectionTitle>Date</SectionTitle>
              <FilterChipsRow>
                <FilterChip $active={activeFilter === 'today'} onClick={() => toggleDateFilter('today')}>Today</FilterChip>
                <FilterChip $active={activeFilter === 'next7'} onClick={() => toggleDateFilter('next7')}>Next 7 Days</FilterChip>
                <FilterChip $active={activeFilter === 'month'} onClick={() => toggleDateFilter('month')}>This Month</FilterChip>
                <FilterChip $active={activeFilter === 'custom'} onClick={() => toggleDateFilter('custom')}>
                  <CalendarDays size={16} /> {customRangeLabel}
                </FilterChip>
              </FilterChipsRow>
              {activeFilter === 'custom' && (
                <CustomRangeContainer>
                  <DateLabel>
                    Start
                    <DateInput
                      type="date"
                      value={customRange.start ? DateTime.fromJSDate(customRange.start).toFormat('yyyy-MM-dd') : ''}
                      onChange={e => {
                        const val = e.target.value ? DateTime.fromFormat(e.target.value, 'yyyy-MM-dd').toJSDate() : null;
                        setCustomRange(range => ({ ...range, start: val }));
                      }}
                      max={customRange.end ? DateTime.fromJSDate(customRange.end).toFormat('yyyy-MM-dd') : undefined}
                    />
                  </DateLabel>
                  <DateLabel>
                    End
                    <DateInput
                      type="date"
                      value={customRange.end ? DateTime.fromJSDate(customRange.end).toFormat('yyyy-MM-dd') : ''}
                      onChange={e => {
                        const val = e.target.value ? DateTime.fromFormat(e.target.value, 'yyyy-MM-dd').toJSDate() : null;
                        setCustomRange(range => ({ ...range, end: val }));
                      }}
                      min={customRange.start ? DateTime.fromJSDate(customRange.start).toFormat('yyyy-MM-dd') : undefined}
                    />
                  </DateLabel>
                  <PrimaryButton onClick={() => setCustomRange({ start: null, end: null })}>Clear</PrimaryButton>
                </CustomRangeContainer>
              )}
            </Section>

            <Section>
              <SectionTitle>Employees</SectionTitle>
              <EmployeeFilterChip
                uniqueEmployees={uniqueEmployees}
                selectedEmployees={selectedEmployees}
                onEmployeeToggle={handleEmployeeToggle}
                onRemoveEmployee={handleRemoveEmployee}
                onClearAllEmployees={clearAllEmployees}
              />
            </Section>

            <Section>
              <SectionTitle>Event Type</SectionTitle>
              <FilterChipsRow>
                {(() => {
                  const isActive = eventType === 'TLG';
                  const { bg, color, border } = getEventTypeChipColors('TLG');
                  const style = isActive ? { background: bg, color, border: `1px solid ${border}` } : undefined;
                  return (
                    <FilterChip $active={isActive} style={style} onClick={() => toggleEventType('TLG')}>
                      <Triangle size={16} /> TLG
                    </FilterChip>
                  );
                })()}
                {(() => {
                  const isActive = eventType === 'WeGo';
                  const { bg, color, border } = getEventTypeChipColors('WeGo');
                  const style = isActive ? { background: bg, color, border: `1px solid ${border}` } : undefined;
                  return (
                    <FilterChip $active={isActive} style={style} onClick={() => toggleEventType('WeGo')}>
                      <LandPlot size={16} /> WeGo
                    </FilterChip>
                  );
                })()}
              </FilterChipsRow>
            </Section>

            <Section>
              <SectionTitle>Status</SectionTitle>
              <EventStatusFilters 
                activeFilter={statusFilter}
                onFilterChange={setStatusFilter}
              />
            </Section>
              </SheetContent>
            </BottomSheet>
          </>,
          document.body
        )}
      </MobileOnly>
      
      {viewMode === 'list' && (
        <DesktopOnly>
          <FilterChipsRow>
            <FilterChip $active={eventTypeFilter === 'all'} onClick={() => toggleEventTypeFilter('all')}>
              All Events
            </FilterChip>
            <FilterChip $active={eventTypeFilter === 'my'} onClick={() => toggleEventTypeFilter('my')}>
              My Events
            </FilterChip>
            <FilterChip $active={activeFilter === 'today'} onClick={() => toggleDateFilter('today')}>
              Today
            </FilterChip>
            <FilterChip $active={activeFilter === 'next7'} onClick={() => toggleDateFilter('next7')}>
              Next 7 Days
            </FilterChip>
            <FilterChip $active={activeFilter === 'month'} onClick={() => toggleDateFilter('month')}>
              This Month
            </FilterChip>
            <FilterChip $active={activeFilter === 'custom'} onClick={() => toggleDateFilter('custom')}>
              <CalendarDays size={16} /> Custom Range
            </FilterChip>
            <EmployeeFilterChip
              uniqueEmployees={uniqueEmployees}
              selectedEmployees={selectedEmployees}
              onEmployeeToggle={handleEmployeeToggle}
              onRemoveEmployee={handleRemoveEmployee}
              onClearAllEmployees={clearAllEmployees}
            />
             {(() => {
                  const isActive = eventType === 'TLG';
                  const { bg, color, border } = getEventTypeChipColors('TLG');
                  const style = isActive ? { background: bg, color, border: `1px solid ${border}` } : undefined;
                  return (
                    <FilterChip $active={isActive} style={style} onClick={() => toggleEventType('TLG')}>
                      <Triangle size={16} /> TLG
                    </FilterChip>
                  );
                })()}
                {(() => {
                  const isActive = eventType === 'WeGo';
                  const { bg, color, border } = getEventTypeChipColors('WeGo');
                  const style = isActive ? { background: bg, color, border: `1px solid ${border}` } : undefined;
                  return (
                    <FilterChip $active={isActive} style={style} onClick={() => toggleEventType('WeGo')}>
                      <LandPlot size={16} /> WeGo
                    </FilterChip>
                  );
                })()}
          </FilterChipsRow>
        </DesktopOnly>
      )}
      
      <DesktopOnly>
        {activeFilter === 'custom' && (
          <CustomRangeContainer>
            <DateLabel>
              Start
              <DateInput
                type="date"
                value={customRange.start ? DateTime.fromJSDate(customRange.start).toFormat('yyyy-MM-dd') : ''}
                onChange={e => {
                  const val = e.target.value ? DateTime.fromFormat(e.target.value, 'yyyy-MM-dd').toJSDate() : null;
                  setCustomRange(range => ({ ...range, start: val }));
                }}
                max={customRange.end ? DateTime.fromJSDate(customRange.end).toFormat('yyyy-MM-dd') : undefined}
              />
            </DateLabel>
            <DateLabel>
              End
              <DateInput
                type="date"
                value={customRange.end ? DateTime.fromJSDate(customRange.end).toFormat('yyyy-MM-dd') : ''}
                onChange={e => {
                  const val = e.target.value ? DateTime.fromFormat(e.target.value, 'yyyy-MM-dd').toJSDate() : null;
                  setCustomRange(range => ({ ...range, end: val }));
                }}
                min={customRange.start ? DateTime.fromJSDate(customRange.start).toFormat('yyyy-MM-dd') : undefined}
              />
            </DateLabel>
            <PrimaryButton onClick={() => setCustomRange({ start: null, end: null })}>
              Clear Dates
            </PrimaryButton>
          </CustomRangeContainer>
        )}
      </DesktopOnly>

      <DesktopOnly>
        <EventStatusFilters 
          activeFilter={statusFilter}
          onFilterChange={setStatusFilter}
        />
      </DesktopOnly>
      
      {error && <div>Error: {error}</div>}
      
      {viewMode === 'calendar' ? (
        <div style={{ height: 600, margin: '1rem 0' }}>
          <InfoBox>
            <Info size={18} />
            <span>Events are displayed in their local timezone, not your current timezone</span>
          </InfoBox>
          <Calendar
            localizer={localizer}
            events={filteredCalendarEvents}
            startAccessor="start"
            endAccessor="end"
            style={{ height: 600 }}
            popup
            onSelectEvent={event => window.location.href = `/events/${event.id}`}
            eventPropGetter={event => {
              const status = event.resource?.event_status || event.event_status;
              const { bg, color, border } = getEventStatusColors(status);
              return {
                style: {
                  backgroundColor: bg,
                  color: color,
                  border: `1.5px solid ${border}`,
                  borderRadius: 8,
                  fontWeight: 600,
                  opacity: 1,
                }
              };
            }}
          />
        </div>
      ) : (
        <>
          {/* My Events (filtered client-side) */}
          {eventTypeFilter === 'my' || eventTypeFilter === 'all' ? (
            <>
              <EventsList>
                {paginatedMyEvents.length === 0 ? (
                  <NoResults>No events found</NoResults>
                ) : (
                  paginatedMyEvents.map(event => (
                    <EventCard key={event.id} event={event} eventEmployees={eventEmployeesMap[event.id] || []} isManager={isManager} />
                  ))
                )}
              </EventsList>
              
              {/* Replace old pagination with new Pagination component */}
              <Pagination
                currentPage={currentPageMy}
                totalPages={totalPagesMy}
                totalItems={filteredEvents.length}
                pageSize={pageSize}
                onPageChange={setCurrentPageMy}
              />
            </>
          ) : (
            <>
              <EventsList>
                {events.length === 0 ? (
                  <NoResults>No events found</NoResults>
                ) : (
                  events.map(event => (
                    <EventCard key={event.id} event={event} eventEmployees={event.employees || []} isManager={isManager} />
                  ))
                )}
              </EventsList>
              
              {/* Replace old pagination with new Pagination component */}
              <Pagination
                currentPage={currentPageAll}
                totalPages={totalPages}
                totalItems={totalEvents}
                pageSize={pageSize}
                onPageChange={setCurrentPageAll}
              />
            </>
          )}
        </>
      )}
    </PageContainer>
  );
};

export default EventsPage;